import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, ArrowLeft, Shield, RefreshCw, CheckCircle, Info } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

// Use relative paths for Vercel serverless functions
// This ensures API calls go to /api/payment/* endpoints
const API = '';

export const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Get state from navigation or sessionStorage (fallback)
  const planData = location.state?.plan || (() => {
    try {
      const stored = sessionStorage.getItem('checkoutState');
      if (stored) {
        const parsed = JSON.parse(stored);
        sessionStorage.removeItem('checkoutState'); // Clean up after use
        return parsed.plan;
      }
    } catch (e) {
      // Ignore parse errors
    }
    return null;
  })();
  
  const userDetails = location.state?.userDetails || (() => {
    try {
      const stored = sessionStorage.getItem('checkoutState');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.userDetails;
      }
    } catch (e) {
      // Ignore parse errors
    }
    return null;
  })();
  
  const [formData, setFormData] = useState({
    name: userDetails?.name || '',
    email: userDetails?.email || '',
    phone: userDetails?.phone || ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Redirect if no plan data
  useEffect(() => {
    if (!planData) {
      navigate('/');
    }
  }, [planData, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.name || formData.name.length < 2) {
      toast({
        title: 'Invalid Name',
        description: 'Please enter your full name (at least 2 characters).',
        variant: 'destructive',
      });
      return false;
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return false;
    }
    if (!formData.phone || !/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/.test(formData.phone)) {
      toast({
        title: 'Invalid Phone',
        description: 'Please enter a valid phone number.',
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!razorpayLoaded) {
      toast({
        title: 'Please Wait',
        description: 'Payment gateway is loading. Please try again in a moment.',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Booking amount is always ₹90 for early access
      const amount = 90;

      // Create order via Vercel serverless function
      const orderResponse = await axios.post('/api/payment/create-order', {
        amount: amount,
        plan_name: planData.name,
        user_name: formData.name,
        user_email: formData.email,
        user_phone: formData.phone,
      });

      const { order_id, amount: orderAmount, currency, key_id } = orderResponse.data;

      // Razorpay options
      const options = {
        key: key_id,
        amount: orderAmount,
        currency: currency,
        name: 'Snapcom',
        description: `${planData.name} - Early Access Booking`,
        order_id: order_id,
        handler: async (response) => {
          try {
            // Verify payment via Vercel serverless function
            const verifyResponse = await axios.post('/api/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_name: formData.name,
              user_email: formData.email,
              user_phone: formData.phone,
              plan_name: planData.name,
              amount: amount,
            });

            if (verifyResponse.data.success) {
              toast({
                title: '🎉 Payment Successful!',
                description: 'Check your email for confirmation. Access granted on Jan 15, 2026.',
              });
              
              // Redirect to success page or home after 3 seconds
              setTimeout(() => navigate('/'), 3000);
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast({
              title: 'Verification Failed',
              description: 'Payment completed but verification failed. Please contact support.',
              variant: 'destructive',
            });
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: '#EAB308',
        },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
            toast({
              title: 'Payment Cancelled',
              description: 'You can complete the payment anytime.',
            });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Payment initiation failed:', error);
      toast({
        title: 'Payment Failed',
        description: error.response?.data?.detail || 'Failed to initiate payment. Please try again.',
        variant: 'destructive',
      });
      setIsProcessing(false);
    }
  };

  if (!planData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black py-20">
      {/* Background decorations */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-float" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-400 hover:text-yellow-500 transition-colors duration-200 mb-8"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Secure Your <span className="text-gradient">Early Access</span>
            </h1>
            <p className="text-xl text-gray-400">
              Complete your booking and lock in exclusive early access pricing
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left: Order Summary */}
            <div className="space-y-6">
              <div className="glass rounded-3xl p-8 border border-yellow-500/30">
                <h2 className="text-2xl font-bold text-white mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Plan Selected:</span>
                    <span className="text-white font-semibold">{planData.name}</span>
                  </div>
                  
                  {planData.originalPrice && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Regular Price:</span>
                      <span className="text-gray-500 line-through">{planData.originalPrice}/month</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Your Price:</span>
                    <span className="text-yellow-500 font-bold text-xl">{planData.price}/month</span>
                  </div>

                  <div className="border-t border-gray-800 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-semibold">Early Access Booking:</span>
                      <span className="text-yellow-500 font-bold text-2xl">₹90</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">One-time payment to secure your early access spot</p>
                  </div>
                </div>

                {/* Important Information */}
                <div className="space-y-4 border-t border-gray-800 pt-6">
                  <div className="flex items-start space-x-3">
                    <Info className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-300">
                        <span className="font-semibold text-white">This amount locks your 50% discount for 6 months</span> and will be adjusted in your first payment after launch (Jan 15, 2026).
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <RefreshCw className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-300">
                        Changed your mind? Write to <a href="mailto:support@snapcom.in" className="text-yellow-500 hover:underline">support@snapcom.in</a> for a full refund. <span className="font-semibold text-white">No questions asked.</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-300">
                        Secure your spot as an <span className="font-semibold text-white">Early Adopter</span> with lifetime recognition in our community.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Badge */}
              <div className="glass rounded-2xl p-6 border border-gray-800">
                <div className="flex items-center space-x-3 mb-3">
                  <Shield className="w-6 h-6 text-yellow-500" />
                  <h3 className="text-white font-semibold">Secure Payment</h3>
                </div>
                <p className="text-sm text-gray-400">
                  Your payment is processed securely through Razorpay with bank-level encryption. We never store your card details.
                </p>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="glass rounded-3xl p-8 border border-gray-800">
              <h2 className="text-2xl font-bold text-white mb-6">Your Details</h2>
              
              <form onSubmit={handlePayment} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white text-base">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isProcessing}
                    className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:ring-yellow-500 h-12"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white text-base">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isProcessing}
                    className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:ring-yellow-500 h-12"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white text-base">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={isProcessing}
                    className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 focus:ring-yellow-500 h-12"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isProcessing || !razorpayLoaded}
                  className="w-full btn-primary bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg py-6 pulse-glow"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing Payment...
                    </>
                  ) : (
                    'Pay ₹90 & Secure Your Spot'
                  )}
                </Button>

                <p className="text-gray-500 text-xs text-center">
                  By proceeding, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};